<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import Icon from '@/components/partials/Icon.vue'
import { Distribution } from '@/enums/Distribution'
import type { MarketplaceEstablishment } from '@/http/models/MarketplaceEstablishment'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import type { Establishment as MarketingPageEstablishment } from '@/http/models/MarketingPage'
import { getCountry } from "@/router/utils/getCountry"
import { useSessionStore } from "@/stores/useSessionStore"
import { storeToRefs } from "pinia"

const sessionStore = useSessionStore()

const props = defineProps<{
  establishment: MarketplaceEstablishment | MarketingPageEstablishment
}>()
const { distribution } = storeToRefs(sessionStore)

const categories = props.establishment.categories.map(c => c.name).join(' • ')

const image = props.establishment.media.images.cover.medium.url === '' ? null : props.establishment.media.images.cover.medium.url

const avatar = props.establishment.media.images.avatar.medium.url === '' ? null : props.establishment.media.images.avatar.medium.url

const partitions = props.establishment.title.replace(/[^a-zA-Z0-9 ]/g, '').split(' ').filter((value: string) => value)
const logoCharacters = partitions ? `${partitions[0][0]}${partitions[1] ? partitions[1][0] : ''}` : ''

const prepTime = props.establishment.meta?.prepTime ?? null

const takeawayDistance = props.establishment.meta?.distance ?? null

const minimal_order_value = props.establishment.meta?.minimal_order_value ?? null

const deliveryCosts = props.establishment.meta?.delivery_costs ?? null

const discount = props.establishment.meta?.activeDiscount?.formattedValue ?? null

const reviews = props.establishment.reviews

const isNew = props.establishment.reviews.count < 2

const isExclusive = props.establishment.meta?.isExclusive ?? false

const cardOverlayClosed = props.establishment.state === null ? false : props.establishment.state === 'closed'

const { t } = useI18n()

const { localizedRoute } = useLocalizedRouter()

const country = props.establishment.city.country.toLowerCase()

// eslint-disable-next-line vue/no-setup-props-destructure
const route = localizedRoute({
  name: 'establishment',
  params: {
    country: country !== getCountry().value ? country : '',
    city: props.establishment.city.slug,
    slug: props.establishment.slug,
  },
})
</script>

<template>
  <RouterLink :to="route" v-taptic="'medium'" class="establishment-card">
    <div class="establishment-card__card">
      <div class="establishment-card__visual">
        <div :class="cardOverlayClosed ? 'establishment-card__image establishment-card__image--closed' : 'establishment-card__image'"
             :style="{ backgroundImage: `url('${image}')` }"
             v-if="image !== null">
        </div>
        <div class="establishment-card__image" v-else></div>
        <div :class="cardOverlayClosed ? 'establishment-card__pills establishment-card__pills--closed' : 'establishment-card__pills'">
          <template v-if="isExclusive">
            <img class="establishment-card__pill--exclusive-label" src="/assets/img/brand/bistroo-exclusive.svg"/>
          </template>

          <div v-if="!isExclusive && minimal_order_value" class="establishment-card__pill">
            <Icon name="shopping-bag-fill"/>
            <span class="bold">{{ minimal_order_value }}</span>
          </div>

          <!--If there is a singlecard the exclusive label and minimum order value fit correctly so they can be displayed together-->
          <div v-else-if="isExclusive && minimal_order_value"
               class="establishment-card__pill establishment-card__minimum-order-value">
            <Icon name="shopping-bag-fill"/>
            <span class="bold">{{ minimal_order_value }}</span>
          </div>
          <div class="establishment-card__pill establishment-card__pill--highlighted">
            <Icon name="star_current_color"/>
            <template v-if="isNew">
              <span class="bold">NEW</span>
            </template>
            <template v-else>
              <span class="bold">{{ reviews.averageRating }}</span>
              ({{ reviews.count }})
            </template>
          </div>
        </div>
      </div>
      <div class="establishment-card__content">
        <div class="establishment-card__establishment-logo"
             :class="{ 'establishment-card__establishment-logo--closed': cardOverlayClosed }"
             :style="{ backgroundImage: `url('${avatar || ''}')` }">
          {{ avatar ? '' : logoCharacters }}
        </div>

        <div class="establishment-card__title">
          {{ establishment.title }}
        </div>

        <div class="establishment-card__subtitle">
          {{ categories }}
        </div>

        <div v-if="!cardOverlayClosed" class="establishment-card__meta">
          <template v-if="distribution === Distribution.TakeAway">
            <div class="establishment-card__meta-item" v-if="takeawayDistance">
              <Icon name="navigation-fill"/>
              {{ takeawayDistance }}
            </div>
            <div class="establishment-card__meta-item" v-if="prepTime">
              <Icon name="time-fill"/>
              {{ prepTime }}
            </div>
          </template>
          <template v-if="distribution === Distribution.Delivery">
            <div class="establishment-card__meta-item" v-if="delivery_costs">
              <Icon name="e-bike-2-fill"/>
              {{ deliveryCosts }}
            </div>
            <div class="establishment-card__meta-item" v-if="prepTime">
              <Icon name="time-fill"/>
              {{ prepTime }}
            </div>
          </template>
          <div class="establishment-card__meta-item establishment-card__meta-item--highlighted"
               v-if="discount !== null">
            <Icon name="price-tag-3-fill"/>
            {{ discount }}
          </div>
        </div>
        <div v-else class="establishment-card__meta">
          <div class="establishment-card__meta-item establishment-card__meta-item--highlighted">
            <Icon name="time-fill-closed"/>
            <p>{{ t('terms.closed') }}</p>
          </div>
        </div>
      </div>
    </div>
  </RouterLink>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.establishment-card {
  $self:  &;
  position: relative;
  height: max-content;

  &__minimum-order-value {
    display: none !important;

    @include md-down {
      display: flex !important;
    }

    @media (min-width: $break-lg) and (max-width: $break-xl) {
      display: flex !important;
    }
  }

  &__card {
    position:         relative;
    z-index:          2;
    box-shadow:       0 0.25rem 0.25rem rgba(0, 0, 0, 0.025);
    border:           1px solid #dee0e4;
    border-radius:    1rem;
    background-color: #ffffff;
    transition:       all 0.2s;
  }

  &__closed-text {
    position:   absolute;
    top:        25%;
    left:       50%;
    transform:  translate(-50%, -25%);
    text-align: center;
    font-weight: 400;
    color:      #ffffff;
    font-size:  1.2rem;

    @include md-down {
      font-size: 1.3rem;
    }

    @include xl-up {
      padding-left: 1.5rem;
    }

    @media (min-width: $break-md) and (max-width: $break-lg) {
      padding-left: 1rem;
    }
  }

  &__image {
    mask:                   url("/assets/img/marketplacepage/establishment-card-mask.svg");
    mask-position:          bottom center;
    mask-repeat:            no-repeat;
    mask-size:              100% auto;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;

    width:                  100%;
    height:                 9.5rem;
    background-size:        cover;
    background-position:    center;
    background-color:       #e9ecf2;

    &--closed {
      filter: brightness(0.5);
    }
  }

  &__pills {
    position:  absolute;
    top:       1rem;
    right:     1rem;
    max-width: calc(100% - 2rem);
    display:   flex;
    gap:       0.5rem;
    align-items: stretch;

    &--closed {
      filter: brightness(0.5);
    }
  }

  &__pill {
    padding:          0.45rem 0.65rem;
    border-radius:    99999px;
    background-color: #ffffff;
    font-weight:      500;
    line-height:      100%;
    font-size:        0.875rem;
    display:          flex;
    align-items:      center;
    gap:              0.25rem;
    box-shadow:       0 0.15rem 0.25rem 0.15rem rgba(0, 0, 0, 0.1);

    &--exclusive-label {
      width:         11.6rem;
      height:        100%;
      border-radius: 999px;
      background-repeat: no-repeat;
      box-shadow:    0 0.15rem 0.25rem 0.15rem rgba(0, 0, 0, 0.1);
    }

    &--highlighted {
      color: var(--color-brand-primary);
    }
  }

  &__content {
    position: relative;
    padding: 1.15rem 1.5rem 1.5rem 1.5rem;
  }

  &__establishment-logo {
    $size:           5.75rem;
    position:        absolute;
    top:             -5.5rem;
    left:            1rem;
    border:          0.5rem solid white;
    width:           $size;
    height:          $size;
    flex:            0 0 $size;
    border-radius:   9999999px;
    background-color: #e3e3e3;
    display:         flex;
    align-items:     center;
    justify-content: center;
    font-weight:     700;
    color:           #ffffff;
    font-size:       1.5rem;
    text-shadow:     1px 1px 2px rgba(0, 0, 0, 0.1);
    box-shadow:      1px 1px 0.5rem rgba(0, 0, 0, 0.1);
    background-size: cover;

    &--closed {
      filter: brightness(0.5);
    }
  }

  &__title {
    font-weight: 700;
    line-height: 150%;
    font-size:   1.14285rem;
    height:      1.7142rem;
    overflow:    hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__subtitle {
    height:      1.5rem;
    overflow:    hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__subtitle,
  &__meta {
    font-weight: 500;
  }

  &__meta {
    display:   flex;
    margin-top: 0.75rem;
    gap:       0.5rem 1.5rem;
    flex-wrap: wrap;

    @include xxl-up {
      font-size: 0.875rem;
    }
  }

  &__meta-item {
    display: flex;
    gap:     0.35rem;
    align-items: center;

    &--highlighted {
      color: var(--color-brand-primary);
    }
  }

  &__amount-of-orders {
    white-space: nowrap;
  }

  &:deep(.review-stars) {
    width: auto;
    justify-content: flex-start;
  }

  &:before {
    content:    "";
    z-index:    1;
    position:   absolute;
    top:        -0.5rem;
    left:       -0.5rem;
    border-radius: 1.5rem;
    width:      calc(100% + 1rem);
    height:     calc(100% + 1rem);
    @include gradient-button($new-gradient-brand-main-10);
    transform:  scale(0.75);
    transition: all 0.2s;
  }

  &:hover {
    #{$self}__card {
      border-color: var(--color-brand-primary);
    }

    &:before {
      transform: scale(1);
    }
  }
}
</style>
